import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const CarrierAgreement = () => {
  return (
    <LegalPageLayout
      title="Carrier Agreement"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        This Carrier Agreement applies to drivers, owner-operators,
        motor carriers, and transportation companies using
        HaggleFreight as Transportation Providers. It supplements
        the Terms of Service and applicable policies when
        presented and accepted.
      </Typography>

      <Typography paragraph>
        This agreement is with GoCourier Inc., operator of
        HaggleFreight. A person acting for a transportation
        company must have authority for the activities performed
        through the account and accurately identify the business
        providing the service.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Independent Carrier Relationship
      </Typography>

      <Typography paragraph>
        HaggleFreight provides marketplace software. Transportation
        Providers decide which opportunities to consider, what
        prices to offer, and whether to enter transportation
        agreements.
      </Typography>

      <Typography paragraph>
        GoCourier Inc. does not perform the physical transportation
        offered by providers. This agreement does not appoint us
        to accept loads or sign transportation contracts in your
        name.
      </Typography>

      <Typography paragraph>
        The intended independent relationship does not override
        classifications or responsibilities based on actual
        operations and applicable law. A driver may be an employee
        or authorized representative of the performing carrier;
        Platform use does not determine that relationship.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Carrier Responsibilities
      </Typography>

      <Typography paragraph>
        Accurately identify the business and personnel performing
        transportation. Provide truthful account, vehicle,
        qualification, insurance, and service information and
        update material changes.
      </Typography>

      <Typography paragraph>
        Review the cargo, locations, contacts, timing, equipment,
        loading requirements, and special instructions before
        making a commitment. Accept only work you can lawfully
        and safely perform.
      </Typography>

      <Typography paragraph>
        Communicate material delays, damage, or inability to
        perform promptly. Do not misrepresent another carrier's
        authority, insurance, or responsibility for a shipment.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Licensing and Compliance
      </Typography>

      <Typography paragraph>
        Maintain the operating authority, licenses, registrations,
        permits, qualifications, and insurance required for your
        particular operation. Different vehicles, cargo, routes,
        and services may have different requirements.
      </Typography>

      <Typography paragraph>
        Account approval is not a legal determination that your
        operation is authorized. Notify affected customers and
        HaggleFreight of a material change that prevents lawful
        performance.
      </Typography>

      <Typography paragraph>
        Do not substitute an undisclosed performing business or
        transfer, subcontract, or re-broker a shipment contrary
        to its accepted terms or applicable law. Obtain necessary
        customer agreement and legally required authority before
        changing the performing business.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Vehicles and Equipment
      </Typography>

      <Typography paragraph>
        Use roadworthy vehicles and equipment suitable for the
        cargo, route, dimensions, weight, loading arrangements,
        and special-handling requirements.
      </Typography>

      <Typography paragraph>
        You are responsible for applicable inspections,
        maintenance, personnel qualifications, securement, and
        safe operation. Do not accept loads exceeding lawful
        capacity or requiring equipment or qualifications you
        do not have.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. Shipment Acceptance
      </Typography>

      <Typography paragraph>
        A transportation commitment arises when the parties
        mutually accept the material terms and complete clearly
        disclosed booking conditions. A preliminary bid or search
        result does not by itself commit the Customer.
      </Typography>

      <Typography paragraph>
        After confirmation, perform according to the accepted
        agreement and applicable law. Material changes require
        agreement from affected parties. Declining an unaccepted
        opportunity does not itself create a penalty or
        minimum-work obligation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Pricing and Negotiation
      </Typography>

      <Typography paragraph>
        You may submit bids and negotiate with Customers.
        Automated suggestions are informational, not mandatory
        rates or earnings guarantees.
      </Typography>

      <Typography paragraph>
        Transportation charges are distinct from HaggleFreight
        Platform charges. This agreement does not establish a
        future subscription price, fixed free-access period,
        commission, or undisclosed deduction.
      </Typography>

      <Typography paragraph>
        Paid Platform offers require the disclosures and
        authorization described in the Terms. General acceptance
        does not authorize an unknown future charge.
        Additional transportation charges require an applicable
        contractual or legal basis.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. Cargo Responsibility
      </Typography>

      <Typography paragraph>
        Exercise the care and comply with the custody and cargo
        duties imposed by applicable law and the accepted
        transportation agreement. Take appropriate steps to
        prevent loss, damage, theft, contamination, and unsafe
        handling.
      </Typography>

      <Typography paragraph>
        Maintain required shipping documents and communicate
        significant cargo issues promptly. Do not abandon cargo,
        dispose of it without lawful authority, or withhold it
        unlawfully during a dispute.
      </Typography>

      <Typography paragraph>
        This agreement does not itself establish a released-value
        limit or waive nonwaivable transportation remedies.
        Cargo responsibility is not eliminated by a Platform
        completion status.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Delivery Records
      </Typography>

      <Typography paragraph>
        Provide accurate delivery records required by the accepted
        transaction or an enabled feature. Records may include
        photographs, signatures, timestamps, notes, and relevant
        location information, subject to applicable disclosures
        and permissions.
      </Typography>

      <Typography paragraph>
        Do not fabricate records, obtain signatures deceptively,
        or upload unnecessary private information. A delivery
        record is evidence, not automatically conclusive proof
        that every contractual obligation was fulfilled.
      </Typography>

      <Typography paragraph>
        Platform records do not replace bills of lading, permits,
        claim documents, or other records required by law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. Payments and Payouts
      </Typography>

      <Typography paragraph>
        When supported payment features are enabled, review the
        provider, processing charges, payout conditions, and timing
        disclosures before using the feature. Provide accurate,
        authorized payout-account information and complete
        required onboarding.
      </Typography>

      <Typography paragraph>
        Stripe Connect users must accept the applicable Stripe
        Connected Account Agreement and other applicable Stripe
        terms presented during onboarding.
      </Typography>

      <Typography paragraph>
        Deductions, reversals, payment reviews, or temporary
        restrictions require a lawful basis and applicable
        disclosed terms. This agreement does not authorize
        arbitrary withholding or confiscation of amounts owed
        to you.
      </Typography>

      <Typography paragraph>
        Pending payouts depend on the relevant payment and
        disclosed conditions. Bank or provider processing may
        affect timing, but GoCourier Inc. remains responsible for
        actions and legal obligations within its responsibility.
        A payment status is not a guaranteed-payment or escrow
        protection.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Taxes
      </Typography>

      <Typography paragraph>
        You are responsible for taxes, invoices, financial records,
        and reporting obligations imposed on you. Provide accurate
        tax information when lawfully required for a payment
        feature.
      </Typography>

      <Typography paragraph>
        GoCourier Inc. retains any separate reporting, withholding,
        or other duties imposed on it by law. Platform information
        is not individualized tax advice.
      </Typography>

      <Typography variant="h5" gutterBottom>
        11. Insurance
      </Typography>

      <Typography paragraph>
        Maintain insurance required by law and your accepted
        services. Confirm that the actual business, vehicles,
        personnel, cargo, and operation are covered where
        necessary.
      </Typography>

      <Typography paragraph>
        GoCourier Inc. does not itself provide insurance, and
        uploading a certificate does not guarantee coverage for
        a particular claim. Do not misrepresent coverage or
        conceal a material cancellation or lapse.
      </Typography>

      <Typography variant="h5" gutterBottom>
        12. Safety
      </Typography>

      <Typography paragraph>
        Comply with applicable vehicle, traffic, cargo, inspection,
        and hours-of-service requirements. Do not perform work
        that requires unsafe or unlawful conduct.
      </Typography>

      <Typography paragraph>
        Do not use the app while driving when unsafe or prohibited.
        Customer instructions, automated routes, suggested
        arrival times, and commercial pressure do not override
        safety obligations.
      </Typography>

      <Typography paragraph>
        Good-faith safety reports or refusal of unlawful work
        are not themselves grounds for punishment. Duties
        concerning cargo already in your custody still apply.
      </Typography>

      <Typography variant="h5" gutterBottom>
        13. Prohibited Conduct
      </Typography>

      <Typography paragraph>
        Follow the Acceptable Use Policy. Do not transport
        prohibited cargo, falsify credentials, impersonate another
        carrier, commit fraud, harass users, or manipulate
        transaction records.
      </Typography>

      <Typography paragraph>
        Protect customer, recipient, facility, and shipment
        information. Do not sell contact information, send
        unsolicited marketing, or disclose private locations
        unnecessarily. Agreement to this contract is not
        permission for undisclosed tracking or unrelated uses
        of your own personal information.
      </Typography>

      <Typography variant="h5" gutterBottom>
        14. Cancellations
      </Typography>

      <Typography paragraph>
        Follow the Cancellation Policy and notify the Customer
        promptly when you cannot perform. Financial consequences
        require an applicable accepted term or legal basis.
      </Typography>

      <Typography paragraph>
        After taking custody, address safe custody and further
        instructions with the authorized Customer. Cancellation
        does not authorize abandonment, unsafe redirection, or
        unlawful withholding of cargo.
      </Typography>

      <Typography variant="h5" gutterBottom>
        15. Disputes
      </Typography>

      <Typography paragraph>
        Provide truthful information and preserve relevant
        agreements, documents, messages, and payment records.
        Support may assist with Platform records and actions
        within its responsibility.
      </Typography>

      <Typography paragraph>
        Support review is not binding arbitration and does not
        replace carrier, insurer, bank, regulator, or court
        processes. Do not allow a formal deadline to expire
        while waiting for support.
      </Typography>

      <Typography variant="h5" gutterBottom>
        16. Platform Role and Legal Responsibilities
      </Typography>

      <Typography paragraph>
        HaggleFreight supplies marketplace tools rather than
        physically performing the transportation. Users remain
        responsible for the decisions and obligations they
        undertake.
      </Typography>

      <Typography paragraph>
        This description is not a legal determination that any
        party is exempt from transportation, payment, employment,
        or other regulation. Applicable law and actual operations
        control. No wording here eliminates GoCourier Inc.'s own
        legal responsibilities.
      </Typography>

      <Typography variant="h5" gutterBottom>
        17. Account Restrictions and Deletion
      </Typography>

      <Typography paragraph>
        Access may be restricted for legitimate, proportionate
        reasons under the Terms. A good-faith complaint, payment
        dispute, safety report, or exercise of privacy rights is
        not itself abuse.
      </Typography>

      <Typography paragraph>
        You may request deletion through the process in the Terms
        and Privacy Policy. An outstanding payout or transaction
        does not remove that ability. Legally required records
        and amounts lawfully due are handled separately.
      </Typography>

      <Typography variant="h5" gutterBottom>
        18. Contact
      </Typography>

      <Typography paragraph>
        Contact support@hagglefreight.com for assistance.
        Do not send passwords or full bank credentials through
        ordinary messages.
      </Typography>

      <Typography paragraph>
        The Terms of Service govern changes, document priority,
        applicable liability and indemnification provisions,
        and disputes. Nothing here removes a nonwaivable right
        or overrides a legal responsibility of any party.
      </Typography>
    </LegalPageLayout>
  );
};

export default CarrierAgreement;
